import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';

import {
    loadCallOutcomes,
    loadFrequencyDetails,
    loadHoursPerWeekDetails,
    loadJobSectorDetails,
    loadOwnPlacementDetails,
    loadPlacementEmploymentTypeDetails,
    loadPlacementEndStatusDetails,
    loadPlacementStartStatusDetails,
    loadWorkConfidence
} from '../../../../store/directusService';
import { loadContractConstants } from '../../../../store/participantService';
import { loadPlacementsForParticipant } from '../../../../store/recruitmentService';
import { loadUsersByServiceIds } from '../../../../store/userService';
import { getNameFromId } from '../../../../utils/directusFunctions';
import {
    CALL_OUTCOME_ERROR,
    FREQUENCY_DETAILS_ERROR,
    HOURS_PER_WEEK_DETAILS_ERROR,
    JOB_SECTOR_DETAILS_ERROR,
    OWN_PLACEMENT_DETAILS_ERROR,
    PLACEMENT_EMPLOYMENT_TYPE_DETAILS_ERROR,
    PLACEMENT_END_STATUS_DETAILS_ERROR,
    PLACEMENT_START_STATUS_DETAILS_ERROR,
    WORK_CONFIDENCE_DETAILS_ERROR
} from '../../../../utils/formValidation/loadingErrorMessageConstants';
import { hasRole, MANAGER, QUALITY, SUPERUSER } from '../../../../utils/userRoles';
import Button from '../../../formElements/Button';
import AccordionHeader from '../../../ui/accordion/AccordionHeader';
import AccordionIcon from '../../../ui/accordion/AccordionIcon';
import LoadingSpinner from '../../../ui/LoadingSpinner';

import AddOwnPlacementEditor from './placement/AddOwnPlacementEditor';
import Placement from './placement/Placement';

import app from '../../../../app.module.css';
import local from '../submissionStyles/placement.module.css';

const participantClosedStatusRoles = [MANAGER, QUALITY, SUPERUSER];

const PlacementManagement = ({ isOpenLastCreatedPlacement, updateIsOpenLastCreatedPlacement }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const [expanded, setExpanded] = useState('');
    const [rows, setRows] = useState([]);
    const [isOpenOwnPlacementEditor, setIsOpenOwnPlacementEditor] = useState(false);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const contractConstants = useSelector(
        (state) => state.entities.participantService.contractConstants
    );
    const placements = useSelector((state) => state.entities.recruitmentService.placements);
    const currentPlacement = useSelector(
        (state) => state.entities.recruitmentService.currentPlacement
    );
    const callOutcomes = useSelector((state) => state.entities.directusService.callOutcomes);
    const frequencyDetails = useSelector(
        (state) => state.entities.directusService.frequencyDetails
    );
    const hoursPerWeekDetails = useSelector(
        (state) => state.entities.directusService.hoursPerWeekDetails
    );
    const jobSectorDetails = useSelector(
        (state) => state.entities.directusService.jobSectorDetails
    );
    const ownPlacementDetails = useSelector(
        (state) => state.entities.directusService.ownPlacementDetails
    );
    const participantStatusDetails = useSelector(
        (state) => state.entities.directusService.participantStatusDetails
    );
    const placementEmploymentTypeDetails = useSelector(
        (state) => state.entities.directusService.placementEmploymentTypeDetails
    );
    const placementEndStatusDetails = useSelector(
        (state) => state.entities.directusService.placementEndStatusDetails
    );
    const placementStartStatusDetails = useSelector(
        (state) => state.entities.directusService.placementStartStatusDetails
    );
    const workConfidence = useSelector((state) => state.entities.directusService.workConfidence);

    // USE EFFECTS
    useEffect(() => {
        currentParticipant?.serviceId &&
            dispatch(loadUsersByServiceIds([currentParticipant.serviceId]));
        if (
            currentParticipant?.contractId &&
            (Object.keys(contractConstants)?.length < 1 ||
                contractConstants.contractId !== currentParticipant?.contractId)
        )
            dispatch(loadContractConstants(currentParticipant?.contractId));
        callOutcomes?.length < 1 && dispatch(loadCallOutcomes());
        placements?.length < 1 && dispatch(loadPlacementsForParticipant(currentParticipant.id));
        ownPlacementDetails?.length < 1 && dispatch(loadOwnPlacementDetails());
        placementEmploymentTypeDetails?.length < 1 &&
            dispatch(loadPlacementEmploymentTypeDetails());
        placementStartStatusDetails?.length < 1 && dispatch(loadPlacementStartStatusDetails());
        placementEndStatusDetails?.length < 1 && dispatch(loadPlacementEndStatusDetails());
        hoursPerWeekDetails?.length < 1 && dispatch(loadHoursPerWeekDetails());
        jobSectorDetails?.length < 1 && dispatch(loadJobSectorDetails());
        frequencyDetails?.length < 1 && dispatch(loadFrequencyDetails());
        workConfidence?.length < 1 && dispatch(loadWorkConfidence());
        if (isOpenLastCreatedPlacement) {
            setExpanded(currentPlacement.id);
            updateIsOpenLastCreatedPlacement(false);
        }
    }, []);

    useEffect(() => {
        if (placements?.length < 1) return;
        setRows([...placements]);
    }, [placements]);

    // HELPER FNS
    const loadingError = () => {
        if (callOutcomes?.length < 1) return CALL_OUTCOME_ERROR;
        if (placementEmploymentTypeDetails?.length < 1)
            return PLACEMENT_EMPLOYMENT_TYPE_DETAILS_ERROR;
        if (ownPlacementDetails?.length < 1) return OWN_PLACEMENT_DETAILS_ERROR;
        if (placementEndStatusDetails?.length < 1) return PLACEMENT_END_STATUS_DETAILS_ERROR;
        if (placementStartStatusDetails?.length < 1) return PLACEMENT_START_STATUS_DETAILS_ERROR;
        if (hoursPerWeekDetails?.length < 1) return HOURS_PER_WEEK_DETAILS_ERROR;
        if (jobSectorDetails?.length < 1) return JOB_SECTOR_DETAILS_ERROR;
        if (frequencyDetails?.length < 1) return FREQUENCY_DETAILS_ERROR;
        if (workConfidence?.length < 1) return WORK_CONFIDENCE_DETAILS_ERROR;
    };

    // EVENT HANDLERS
    const onAccordionChange = (panel) => setExpanded(expanded === panel ? '' : panel);

    // AWAITING CONTENT
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    // RENDER
    return (
        <div className={local.placementsWrapper}>
            <h2 className={app.sectionHeading}>Placements</h2>
            <Button
                id="addOwnPlacementButton"
                type="button"
                disabled={
                    getNameFromId(participantStatusDetails, currentParticipant.statusId) ===
                        'Closed' && !hasRole(participantClosedStatusRoles, roles)
                }
                content="Add Own Placement"
                icon={true}
                onClick={() => setIsOpenOwnPlacementEditor(true)}
            />

            <AddOwnPlacementEditor
                isOpen={isOpenOwnPlacementEditor}
                openOwnPlacementEditor={setIsOpenOwnPlacementEditor}
            />

            <div className={local.accordionContent}>
                {rows.map((el) => (
                    <div key={el.id}>
                        <Accordion
                            expanded={expanded === el.id}
                            onChange={() => onAccordionChange(el.id)}
                            className={app.accordionDivider}
                            data-testid="placement-accordions"
                            slotProps={{ transition: { mountOnEnter: true } }}>
                            <AccordionSummary
                                expandIcon={<AccordionIcon />}
                                aria-controls={`panel1a-content`}
                                id={`panel1a-header`}>
                                <AccordionHeader>
                                    <div className={local.accordionTitle}>
                                        <span>{el.code}</span>
                                        <span>{el.businessName}</span>
                                        <span>{el.vacancyTitle}</span>
                                    </div>
                                </AccordionHeader>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Placement placementRow={el} />
                            </AccordionDetails>
                        </Accordion>
                    </div>
                ))}
            </div>
        </div>
    );
};

PlacementManagement.propTypes = {
    isOpenLastCreatedPlacement: PropTypes.bool.isRequired,
    updateIsOpenLastCreatedPlacement: PropTypes.func.isRequired
};

export default PlacementManagement;
